import React from 'react';
import { Box, type BoxProps } from '@mantine/core';

export type TrustBadgeVariant = 'verified' | 'ai-seeded' | 'pending';

export interface TrustBadgeProps extends Omit<BoxProps, 'children'> {
  variant: TrustBadgeVariant;
  /** Override default label (e.g. "Community Verified"). */
  label?: string;
  size?: 'xs' | 'sm';
}

const STYLES: Record<
  TrustBadgeVariant,
  { background: string; border: string; color: string; defaultLabel: string }
> = {
  verified: {
    background: 'rgba(63, 185, 80, 0.15)',
    border: '1px solid rgba(63, 185, 80, 0.35)',
    color: 'var(--gs-green, #3FB950)',
    defaultLabel: 'Verified'
  },
  'ai-seeded': {
    background: 'rgba(137, 87, 229, 0.12)',
    border: '1px solid rgba(137, 87, 229, 0.35)',
    color: 'var(--gs-purple, #8957e5)',
    defaultLabel: 'AI suggested'
  },
  pending: {
    background: 'rgba(210, 153, 34, 0.15)',
    border: '1px solid rgba(210, 153, 34, 0.35)',
    color: 'var(--gs-warning, #D29922)',
    defaultLabel: 'Pending review'
  }
};

/**
 * Consistent trust indicators: community-verified registry data, AI-assisted fields, or pending moderation.
 */
export function TrustBadge ({
  variant,
  label,
  size = 'sm',
  sx,
  ...rest
}: TrustBadgeProps) {
  const theme = STYLES[variant];
  const text = label ?? theme.defaultLabel;
  const fontSize = size === 'xs' ? 9 : 11;
  const padding = size === 'xs' ? '2px 6px' : '4px 10px';

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        padding,
        borderRadius: size === 'xs' ? 3 : 4,
        fontSize,
        fontWeight: 700,
        fontFamily: 'DM Sans, sans-serif',
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        lineHeight: 1.3,
        background: theme.background,
        border: theme.border,
        color: theme.color,
        ...sx
      }}
      {...rest}
    >
      {variant === 'verified' ? `✓ ${text}` : text}
    </Box>
  );
}
